'use client'

import { useState } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { UserButton } from "@clerk/nextjs";
import { LayoutDashboard, Download } from "lucide-react";

type NavLink = {
  href: string;
  label: string;
  className?: string;
};

export function MobileNav({
  links,
  isAuthenticated,
}: {
  links: NavLink[];
  isAuthenticated: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="md:hidden flex items-center gap-2">
      {/* Checklist Gratis — always visible on mobile */}
      {!isAuthenticated && (
        <Link
          href="/checklist-sopravvissuto"
          onClick={() =>
            posthog.capture("click_checklist_lead", {
              location: "header_mobile",
              value_prop: "12 controlli settimanali per non perdere provvigioni",
            })
          }
        >
          <Button
            variant="outline"
            size="sm"
            className="text-honey-700 border-honey-300 hover:bg-honey-50 hover:text-honey-800 font-semibold text-xs"
          >
            <Download className="w-3 h-3 mr-1" />
            Checklist
          </Button>
        </Link>
      )}

      {/* Hamburger toggle */}
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-mocha-600 hover:text-mocha-900 transition-colors"
        aria-label={open ? "Chiudi menu" : "Apri menu"}
        aria-expanded={open}
      >
        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Slide-down menu */}
      {open && (
        <div className="absolute top-full left-0 right-0 bg-white/98 backdrop-blur-md border-b border-ruby-200 shadow-lg z-50">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className={`px-3 py-2.5 rounded-lg text-base font-medium transition-colors hover:bg-ruby-50 ${
                  link.className ?? "text-mocha-600 hover:text-mocha-900"
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="border-t border-ruby-100 mt-2 pt-3 flex flex-col gap-2">
              {isAuthenticated ? (
                <>
                  <Link href="/subscription" onClick={() => setOpen(false)}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-slate-700 hover:text-indigo-600 font-medium"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </Button>
                  </Link>
                  <div className="px-3 py-1">
                    <UserButton afterSignOutUrl="/" />
                  </div>
                </>
              ) : (
                <>
                  <Link
                    href="/sign-in"
                    onClick={() => {
                      setOpen(false);
                      posthog.capture("click_login", {
                        location: "header_mobile",
                      });
                    }}
                    className="px-3 py-2.5 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Accedi
                  </Link>
                  <Link
                    href="/cta"
                    onClick={() => {
                      setOpen(false);
                      posthog.capture("click_prova_gratis", {
                        location: "header_mobile",
                      });
                    }}
                  >
                    <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm">
                      Prova Gratis
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}
